<template>
  <modal
    size="full"
    title="Price Check"
    :open="open"
    @close="closeModal"
  >
    <div class="grid">
      <section>
        <p>Get the estimated value for any card by selecting an index and entering the price paid on a previous date.</p><br />

        <form>
          <div class="step">
            <section class="step-header">
              <h5>Select an index</h5>
            </section>

            <div>
              <p>We will use the index to forecast your card's current value based on the index value today.</p>

              <index-search
                ref="indexSearch"
                @selectPlayer="setPlayer"
                @selectIndex="setIndex"
              />

              <div v-if="index" class="index-list-container">
                <index-list-item :index="index" />
              </div>
            </div>
          </div>

          <div class="step">
            <section class="step-header">
              <h5>Enter the price paid</h5>
            </section>

            <div>
              <currency-input
                v-model="price"
                label="Past Sold Price"
                @keyup.native.enter="submit"
              />
            </div>
          </div>

          <div class="step">
            <section class="step-header">
              <h5>Enter the date purchased</h5>
            </section>

            <div>
              <p>If you can't remember the exact date just get as close as possible.</p>

              <date-input
                v-model="date"
                label="Past Sold Date"
                @keyup.native.enter="submit"
              />
            </div>
          </div>
        </form>
      </section>

      <section>
        <div v-if="output" class="output">
          <value-display
            estimated
            size="large"
            :confidence="output.confidence"
            :value="$filters.currency(output.value, { full: true })"
          />

          <div class="description">
            {{ output.description }}
          </div>
        </div>
      </section>
    </div>

    <template #footer>
      <align justify="space-between">
        <btn :disabled="!output" kind="secondary" @click="clear">
          Start Over
        </btn>

        <btn
          kind="ebay"
          :loading="checking"
          :disabled="isDisabled"
          loading-text="Checking"
          @click="submit"
        >
          Submit
        </btn>
      </align>
    </template>
  </modal>
</template>

<script>
import { getGuideValue } from '@cardladder/shared/utils/collection.util';

import { playerService, indexService } from '@/services';

import IndexListItem from '@cardladder/shared/components/IndexListItem';
import ValueDisplay from '@cardladder/shared/components/ValueDisplay';

import IndexSearch from '@/components/IndexSearch';

export default {
  components: {
    IndexListItem,
    IndexSearch,
    ValueDisplay,
  },

  props: {
    open: Boolean,
  },

  data() {
    return {
      index: null,
      price: null,
      date: null,
      output: null,
      checking: false,
    };
  },

  computed: {
    isDisabled() {
      return !this.index || !this.price || !this.date;
    },
  },

  methods: {
    async setPlayer(player) {
      this.index = await playerService.getPlayer(player.id);
    },

    async setIndex(id) {
      this.index = await indexService.getIndex('indexes', id);
    },

    submit() {
      if (!this.isDisabled) {
        this.checking = true;

        try {
          const guideValue = getGuideValue(this.index, this.date, this.price);

          this.output = guideValue;
        } catch (e) {
          this.$toasted.error('Error determining the value');
        }

        this.checking = false;
      }
    },

    clear() {
      this.index = null;
      this.price = null;
      this.date = null;
      this.output = null;
      this.$refs.indexSearch.clear();
    },

    closeModal() {
      this.clear();
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.grid {
  display: grid;
  grid-gap: $gp;
  grid-template-columns: 5fr 4fr;
  margin-bottom: $gp;

  @media($medium-down) {
    display: block;
  }
}

.step {
  margin-bottom: $gp * 2;
}

.step-header {
  margin-bottom: $gp;

  h5 {
    margin-bottom: 0;
    font-family: $font-family-secondary;
    text-transform: uppercase;
    font-weight: bold;
  }
}

.index-list-container {
  margin: 0 (-$gp);
}

.output {
  @media($large-up) {
    height: 80%;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .description {
    font-size: 18px;
    margin-top: $gp;
  }
}
</style>
